import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Box, Flex } from "../../../components/Flex";
import { Content } from "../../../components/Layout";
import { FilledLabel, Paragraph, Title } from "../../../components/Text";
import { Button } from "../../../components/Button";
import { CloseIcon } from "../../../icons/CloseIcon";
import { RevokeIcon } from "../../../icons/RevokeIcon";
import { DoneIcon } from "../../../icons/DoneIcon";
import { EditIcon } from "../../../icons/EditIcon";
import { Scrollbar } from "../../../components/Scrollbar";
import { filter, find,  size, has, every, partial } from "lodash";
import { SWTYPES } from "../../../settings";
import { Modal } from "../../../components/Modal";
import { Badge } from "../../../components/Circle";
import { CancelVerificationForm } from "./CancelVerificationForm";
import { RemoveIcon } from "../../../icons/RemoveIcon";
import { ExplainIcon } from "../../../icons/ExplainIcon";
import { AuditIcon } from "../../../icons/AuditIcon";


const PkgMassUpdate = ({
    data={},
    loading,
    onClose,
    renderFieldContent,
    updatePackages,
    showForm,
    removePackage,
    pathname,
    search,
    errors={}
}) => {
    const [ visible, setVisible ] = useState(false);

    const fields = [
        { label: "Version", attr: "version" },
        { label: "System Architecture", attr: "sysarch", render: (d) => (
            <Flex gap={{ right: 8 }}>
                {Array.isArray(d)
                    ? d.map(sysarch => (
                        <FilledLabel key={sysarch}>{sysarch}</FilledLabel>
                    ))
                    : d
                }
            </Flex>
        )},
        { label: "OS Versions", attr: "minos", render: (d) => (
            <Flex gap={{ right: 8 }} wrap="wrap" style={{ margin: "-2px"}}>
                {Array.isArray(d)
                    ? d.map(minos => (
                        <FilledLabel key={minos} style={{ margin: "2px"}}>{minos}</FilledLabel>
                    ))
                    : d
                }
            </Flex>
        )},
        { label: "Kit Type", attr: "swtype", render: (d, record) => find(SWTYPES, { key: d.toLowerCase() }) ? find(SWTYPES, { key: d.toLowerCase() }).title + (record.ecoRating ? ` (Rating-${record.ecoRating})` : "") : null }
    ];

    return(
        <>
            <Content background="#FFFFFF" gap={{ bottom: 18 }}>
                <Flex width="100%" justify="space-between" margin={{ bottom: "24px"}}>
                    <Flex align="center" gap={{ right: 8 }}>
                        <Title h4>Packages update</Title>
                    </Flex>
                    <CloseIcon onClick={onClose} />
                </Flex>
                <Flex height="calc(100% - 108px)" justify="space-between" gap={{ right: 18 }}>
                    {size(data)
                        ? <Scrollbar>
                            <Flex direction="column" gap={{ bottom: 12 }} padding={{ right: "24px" }}>
                                {Object.values(data).map((pkg, idx) => {
                                    return every(["key", "verified", "name"], partial(has, pkg))
                                    ? <Flex key={pkg.key} direction="column" gap={{ bottom: 8 }} padding={{ top: "12px" }} style={{ borderTop: idx > 0 && "1px solid var(--sp-light-grey-blue-color)"}}>
                                        <Flex align="center" justify="space-between" gap={{ right: 8 }}>
                                            <Flex align="center" gap={{ right: 8 }}>
                                                <Badge size={8} color={pkg.verified ? "var(--sp-success-color)" : "var(--sp-error-color)"}/>
                                                <Title><Link to={{ pathname: `/packages/${pkg.key}`, state: { from: pathname + search } }}>{pkg.name}</Link></Title>
                                                {pkg.archivedVersion && <FilledLabel filled>archived</FilledLabel>}
                                            </Flex>
                                            <RemoveIcon color="var(--sp-grey-blue-color)" hoverColor="var(--sp-error-hover-color)" size={18} onClick={() => removePackage(pkg)}/>
                                        </Flex>
                                        <Flex gap={{ right: 24 }}>
                                            <Flex direction="column" flex="1" gap={{ bottom: 8 }}>
                                                {renderFieldContent(fields, pkg)}
                                                {errors[pkg.key] && <Flex align="center" gap={{ right: 8 }}>
                                                    <ExplainIcon size={16} />
                                                    <Paragraph color="var(--sp-error-hover-color)">{errors[pkg.key].message}</Paragraph>
                                                </Flex>}
                                            </Flex>
                                            <Box flatten background="var(--sp-light-grey-blue-color)" gap={{ bottom: 8 }} flex="1">
                                                <Title capitalize>package permissions</Title>
                                                {Array.isArray(pkg.contracts) && pkg.contracts.length
                                                    ? <>
                                                        {pkg.contracts.map((contract, idx) => (
                                                            <Paragraph key={`contract-${idx}`}>&mdash;&nbsp;{contract}</Paragraph>
                                                        ))}
                                                        {pkg.ecokits && <Paragraph>&mdash;&nbsp;Rights to ecokits</Paragraph>}
                                                        {pkg.rToMinorRel && <Paragraph>&mdash;&nbsp;Rights to minor releases</Paragraph>}
                                                    </>
                                                    : "No contracts applied yet" }
                                            </Box>
                                        </Flex>
                                    </Flex>
                                    : null
                            })}
                            </Flex>
                        </Scrollbar>
                        : <>No packages to update</>}
                </Flex>
                <Flex justify="space-between">
                    <Flex gap={{ right: 12 }}>
                        <Button disabled={loading} onClick={() => filter(Object.values(data), d => d.verified == true).length == 0 ? updatePackages({ids: Object.keys(data), action: "verify", verify: 1 }) : setVisible(true)}>
                            {filter(Object.values(data), d => has(d, "verified") && d.verified == true).length == 0
                                ? <DoneIcon size={20} />
                                : <RevokeIcon size={20} />}
                            {filter(Object.values(data), d => has(d, "verified") && d.verified == true).length == 0
                                ? "Verify"
                                : "Undo Verify"}

                        </Button>
                        <Button disabled={loading || filter(Object.values(data), d => has(d, "verified") && d.verified == true).length > 0} onClick={() => showForm({ key: Object.keys(data), sysarch: [], osVersions: [], swtype: undefined, ecoRating: null, contracts: [], ecokits: undefined, rToMinorRel: undefined })}>
                            <EditIcon size={20} />
                            {size(data) > 1 ? "Edit Packages" : "Edit Package"}
                        </Button>
                        <Button disabled={loading || new Set(Object.values(data).map(d => has(d, "archivedVersion") && d.archivedVersion == true)).size > 1} onClick={() => updatePackages({ids: Object.keys(data), action: "archive"})}>
                            <AuditIcon size={20} />
                            {filter(Object.values(data), d => has(d, "archivedVersion") && !d.archivedVersion).length == 0
                                ? "Restore"
                                : "Archive"}
                        </Button>
                    </Flex>
                </Flex>
            </Content>
            <Modal visible={visible}>
                <CancelVerificationForm
                    onCancel={() => setVisible(false)}
                    onSubmit={(comment) => updatePackages({ ids: filter(Object.values(data), d => has(d, "verified") && d.verified == true).map(o => o.key), action: "verify", verify: 0, comment: comment })}
                />
            </Modal>
        </>
    );
};

export { PkgMassUpdate };